<template>
  <div class="flex flex-col gap-5">
    <div class="text-xl font-bold">Geräteliste an Plattform senden</div>
    <div>Bitte die aufgenommenen Geräte an die Plattform senden.</div>

    <div class="content-box">
      <div class="progress-container">
        <div style="height: 25px">
          {{ devicePoolSent ? 'Geräteliste wird übermittelt...' : '' }}
        </div>
        <div v-if="devicePoolSent" class="w-full h-3 bg-gray-200 rounded">
          <div
            class="h-full bg-blue-600 rounded transition-[width] duration-100"
            :style="{ width: `${Math.min(Math.max(progress, 0), 100)}%` }"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="Math.round(progress)"
          />
        </div>
        <PrimaryButton
          v-if="isSendDevicePoolAvailable"
          class="w-full"
          label="Geräteliste senden"
          data-testid="sendDeviceListButton"
          @click="startFunkcheck()"
        />
      </div>

      <StatusIndicator
        :is-complete="devicePoolSent"
        label="Geräteliste gesendet"
      />
      <StatusIndicator
        :is-complete="devicePoolReceived"
        label="Geräteliste von Plattform empfangen"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { inject, ref, computed, watch } from 'vue';
import { Notification } from '@/models';
import StatusIndicator from '@/components/Funkcheck/StatusIndicator.vue';
import useStores from '@/composables/useStores';
import PrimaryButton from '@/components/UI/Button/PrimaryButton.vue';

const validateStep = inject('validateStep') as (isValid: boolean) => void;
const devicePoolSent = ref<boolean>(false);
const devicePoolReceived = ref<boolean>(false);
const isFunkcheckLoading = ref<boolean>(false);
const progress = ref<number>(0);

const { entitiesStore, formStore, notificationStore, funkcheckStore } =
  useStores();

const isSendDevicePoolAvailable = computed(
  () => !isFunkcheckLoading.value && !devicePoolReceived.value
);
const isValid = computed(
  () => devicePoolSent.value && devicePoolReceived.value
);

watch(devicePoolReceived, () => {
  if (devicePoolReceived.value) {
    setInterval(() => {
      progress.value = progress.value + 1;
    }, 5);
  }
});

function failSend(error: unknown, reason: string) {
  // The whole failure path used to be a bare `catch {}` plus a generic
  // "IN_F-500" toast, so nothing reached the console — and therefore nothing
  // reached logcat via Capacitor's console bridge. Log first, always.
  console.error(`[Funkcheck] Geräteliste senden failed (${reason})`, error);
  isFunkcheckLoading.value = false;
  devicePoolSent.value = false;
  notificationStore.addNotification(
    new Notification(
      'Der Funkcheckservice steht derzeit nicht zur Verfügung - Sie können den Auftrag dennoch abschließen',
      'IN_F-500',
      'error',
      10_000
    )
  );
}

async function startFunkcheck() {
  // Everything up to `initFunkcheck` used to run outside the try: a missing
  // form id threw, and a gateway the store cannot resolve made
  // `gateway.serialNumber` a TypeError. Both left the click handler as an
  // unhandled rejection — no toast, no log, a button that looks dead.
  let gatewaySerialNumber: string;
  try {
    const installationPointId = formStore.lastForm.id;
    if (!installationPointId) {
      throw new Error('No installationPoint id provided');
    }
    const installationPoint = entitiesStore.getEntityById(installationPointId);
    if (!installationPoint?.activeDeviceId) {
      throw new Error(
        `No active device on installationPoint ${installationPointId}`
      );
    }
    const gateway = entitiesStore.getEntityById(
      installationPoint.activeDeviceId
    );
    if (!gateway?.serialNumber) {
      throw new Error(
        `Gateway ${installationPoint.activeDeviceId} has no serialNumber`
      );
    }
    gatewaySerialNumber = gateway.serialNumber;
  } catch (error) {
    failSend(error, 'gateway lookup');
    return;
  }

  devicePoolSent.value = true;
  isFunkcheckLoading.value = true;

  try {
    await funkcheckStore.initFunkcheck(gatewaySerialNumber);
    setTimeout(() => {
      devicePoolReceived.value = true;
      isFunkcheckLoading.value = false;
      validateStep(true);
    }, 3_000);
  } catch (error) {
    failSend(error, 'initFunkcheck');
  }
}
</script>

<style lang="scss" scoped>
.progress-container {
  display: flex;
  flex-flow: column;
  align-items: center;
  height: 50px;
  margin-bottom: 40px;
}
</style>
